import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const modules = [
  {
    num: '01',
    title: 'Die Anatomie der energetischen Abhängigkeit',
    body: 'Wir tauchen tief in die Neurobiologie der emotionalen Abhängigkeit ein. Du verstehst, warum dein Gehirn eine toxische oder unerfüllte Verbindung buchstäblich wie eine Droge behandelt – und warum Willenskraft allein niemals ausreicht, um diesen Kreislauf zu durchbrechen. Wir identifizieren die exakten Kindheitswunden und frühkindlichen Prägungen, die dazu geführt haben, dass dein Nervensystem Unsicherheit mit Liebe gleichsetzt. Dieser Moment der Erkenntnis ist der erste Atemzug in ein neues Leben.',
  },
  {
    num: '02',
    title: 'Die Aktivierung des Divine Feminine Blueprints',
    body: 'Das Feminine ist keine Schwäche – es ist deine stärkste Superkraft. In diesem Modul reaktivieren wir deinen angeborenen weiblichen Blueprint: die Fähigkeit, tief zu empfangen, zu vertrauen und durch bloße Präsenz anzuziehen, was dir bestimmt ist. Wir lösen die kollektive Konditionierung auf, die Frauen gelehrt hat, sich maskulin zu verhalten, um erfolgreich oder geliebt zu werden. Du lernst, deine weibliche Intuition als unfehlbaren Kompass einzusetzen – in der Liebe wie im Business.',
  },
  {
    num: '03',
    title: 'Magnetismus durch radikales Loslassen',
    body: 'Das Paradox der Anziehung: Je mehr du festhältst, desto mehr verlierst du. In dieser tiefen Übung erlernst du die Kunst des radikalen Loslassens – nicht als Aufgabe, sondern als Akt der höchsten Selbstliebe. Wir arbeiten mit konkreten energetischen Werkzeugen, die du sofort nach der Masterclass anwenden kannst, um alte Bindungen, Erwartungen und Angstmuster zu lösen. Das Resultat ist eine physisch spürbare Leichtigkeit und eine magnetische Ausstrahlung, die keine Strategie der Welt ersetzen kann.',
  },
  {
    num: '04',
    title: 'Der Anti-Hustle Business Code – Skalierung aus der Seele',
    body: 'Für die Unternehmerinnen und Frauen im Business: Wir demontieren den Mythos, dass mehr Arbeit mehr Erfolg bedeutet. Du lernst, wie du dein Business-Energiefeld so ausrichtest, dass Premium-Klientinnen und Chancen magnetisch zu dir fließen – ohne erzwungene Verkaufstrichter, ohne erschöpfende Content-Produktion, ohne das Gefühl, dich jeden Tag neu beweisen zu müssen. Dein Business soll eine Erweiterung deiner Seele sein, kein Käfig aus goldenen Verpflichtungen.',
  },
  {
    num: '05',
    title: 'Die Identitäts-Initiation – Wer du wirklich bist',
    body: 'Der krönende Abschluss der Masterclass ist eine geführte Identitäts-Initiation, in der du die Frau verkörperst, die du bereits bist – unter all den Schichten der Angst, der Konditionierung und der gesellschaftlichen Erwartungen. Du verlässt diesen Raum nicht als dieselbe Person, die ihn betreten hat. Du verlässt ihn als die Frau, die weiß, dass sie genug ist. Dass sie mächtig ist. Dass sie bereit ist, ihr volles Erbe anzunehmen.',
  },
];

const DeepCurriculum = () => {
  const [open, setOpen] = useState(null);

  return (
    <section className="relative py-36 bg-darker border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,rgba(212,175,55,0.04)_0%,transparent_65%)]" />

      <div className="relative z-10 max-w-5xl mx-auto px-6">
        <div className="text-center mb-20">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-gold tracking-[0.35em] uppercase text-xs mb-6 font-semibold"
          >
            Das Curriculum · Was du wirklich lernst
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="text-4xl md:text-6xl font-serif text-cream leading-tight"
          >
            90 Minuten,{' '}
            <span className="italic text-transparent bg-clip-text bg-gold-gradient">
              die alles verändern.
            </span>
          </motion.h2>
        </div>

        <div className="space-y-4">
          {modules.map((mod, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.7, delay: idx * 0.1 }}
              className="relative overflow-hidden cursor-pointer group"
              style={{
                background: open === idx ? 'rgba(212,175,55,0.06)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${open === idx ? 'rgba(212,175,55,0.4)' : 'rgba(255,255,255,0.07)'}`,
                boxShadow: open === idx ? '0 0 40px rgba(212,175,55,0.1), -4px 0 30px rgba(212,175,55,0.15)' : 'none',
                borderRadius: '2px',
                transition: 'all 0.6s ease',
              }}
              onClick={() => setOpen(open === idx ? null : idx)}
            >
              {open === idx && (
                <div className="absolute top-0 left-0 right-0 h-[1px]"
                  style={{ background: 'linear-gradient(to right, transparent, #D4AF37, transparent)' }} />
              )}
              <div className="flex items-center gap-6 p-7">
                <span className="text-3xl font-serif shrink-0"
                  style={{ color: open === idx ? '#D4AF37' : 'rgba(212,175,55,0.25)', transition: 'color 0.5s' }}>
                  {mod.num}
                </span>
                <h3 className="text-xl font-serif flex-1 transition-colors duration-500"
                  style={{ color: open === idx ? '#F9F9F9' : 'rgba(249,249,249,0.75)' }}>
                  {mod.title}
                </h3>
                <motion.span
                  animate={{ rotate: open === idx ? 45 : 0 }}
                  transition={{ duration: 0.4 }}
                  className="text-gold text-2xl shrink-0 font-light"
                >
                  +
                </motion.span>
              </div>
              <AnimatePresence initial={false}>
                {open === idx && (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <p className="px-7 pb-8 text-cream/65 font-light leading-relaxed text-lg" style={{ paddingLeft: 'calc(1.75rem + 3rem + 1.5rem)' }}>
                      {mod.body}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DeepCurriculum;